import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeftRight,
  Plus,
  Calendar,
  Filter,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  LayoutList,
  RefreshCcw,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  Banknote,
  X,
  LayoutGrid,
  Layers,
  TrendingUp,
  Building2,
  DollarSign
} from 'lucide-react';
import localApiAdapter from '../../core/services/localApiAdapter';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import {
  SAUDI_BANKS,
  SAUDI_PRIMARY_BANK_CODES,
  SAUDI_TRANSFER_SYSTEMS,
  SAUDI_TRANSFER_AMOUNT_TIERS,
  bankMatchesCode,
} from './saudiBankingDefaults';
import { mapTransferFromApi, mapTransferToApi } from './bankingApiMappers';
import { exportBankingRows, exportBankingPdf, importBankingRows } from './bankingExportImport';
import { resolveBankingModalFormTabs, resolveBanqueEntityReference } from './bankingHelpers';
import { buildPaymentBeneficiaryOptions } from './bankingSaudiHelpers';
import { normalizeAndSyncBankAccounts } from './bankingTreasurySync';
import { useClients } from '../../core/contexts/ClientsContext';
import { useBanqueConfig, useBanqueListViewMode } from './useBanqueConfig';
import { useSettings } from '../../core/contexts/SettingsContext';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

registerLocale('ar', ar);

const BankTransfersPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.bankTransfersPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const formatAmount = (amount) => formatBrandMoney(amount);

  const saudiBankOptions = SAUDI_BANKS.map((bank) => ({
    value: bank.code,
    label: bank.name,
    isDefault: true,
  }));

  const transferSystemOptions = SAUDI_TRANSFER_SYSTEMS.filter((sys) =>
    ['sarie', 'sadad', 'swift'].includes(sys.value)
  ).map((sys) => ({
    value: sys.value,
    label: sys.labelAr,
    isDefault: sys.value === 'sarie',
  }));

  const statusFormOptions = [
    { value: 'pending', label: p('status_pending'), icon: <Clock size={14} className="text-amber-400" />, isDefault: true },
    { value: 'completed', label: p('status_completed'), icon: <CheckCircle size={14} className="text-green-400" />, isDefault: true },
    { value: 'rejected', label: p('status_rejected'), icon: <XCircle size={14} className="text-red-400" />, isDefault: true },
  ];
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const { clients } = useClients();
  const { settings } = useSettings();
  const bankingConfig = useBanqueConfig();
  const [viewMode, setViewMode] = useBanqueListViewMode(bankingConfig);
  const entityLabel = p('entity_label', { defaultValue: 'التحويل البنكي' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'تحويلات بنكية' });

  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Advanced options
  const [mainViewMode, setMainViewMode] = useState('all_transfers');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const { filters: columnFilters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [columnVisibility, setColumnVisibility] = useState({
    reference: true,
    date: true,
    banqueSource: true,
    beneficiaire: true,
    montant: true,
    frais: true,
  });

  const [transferFormTab, setTransferFormTab] = useState('transferInfo');
  
  // Data
  const [transfers, setTransfers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    reference: '',
    banqueSource: '',
    banqueDestination: '',
    from_account_id: '',
    to_account_id: '',
    transferSystem: 'sarie',
    beneficiaire: '',
    montant: '',
    motif: '',
    frais: '',
    status: ''
  });
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadBankAccounts();
    loadSuppliers();
    loadTransfers();
  }, []);

  const loadSuppliers = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/fournisseurs');
      const raw = Array.isArray(response) ? response : response?.data || [];
      setSuppliers(raw);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setSuppliers([]);
    }
  };

  const beneficiaryOptions = useMemo(
    () => buildPaymentBeneficiaryOptions({
      clients,
      suppliers,
      orders: transfers.map((t) => ({ beneficiaire: t.beneficiaire })),
    }),
    [clients, suppliers, transfers],
  );

  useEffect(() => {
    if (!showModal || editingRecord || !bankAccounts.length) return;
    const fromAcct = bankAccounts[0];
    const toAcct = bankAccounts[1] || bankAccounts[0];
    setFormData((prev) => {
      if (prev.from_account_id && prev.to_account_id) return prev;
      return {
        ...prev,
        from_account_id: String(fromAcct.id),
        to_account_id: String(toAcct.id),
        banqueSource: `${fromAcct.bank_name} — ${fromAcct.account_name}`,
        banqueDestination: `${toAcct.bank_name} — ${toAcct.account_name}`,
        status: prev.status || 'pending',
        date: prev.date || new Date().toISOString().slice(0, 10),
      };
    });
  }, [showModal, editingRecord, bankAccounts]);

  // ===== HELPER FUNCTIONS =====
  const columnStatusFilterOptions = statusFormOptions.map(({ value, label }) => ({ value, label }));

  const loadBankAccounts = async () => {
    try {
      const accounts = await localApiAdapter.get('/banking/accounts');
      setBankAccounts(normalizeAndSyncBankAccounts(accounts));
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      setBankAccounts([]);
    }
  };

  const loadTransfers = async () => {
    setLoading(true);
    try {
      const data = await localApiAdapter.get('/banking/transfers');
      setTransfers(sortRecordsNewestFirst((Array.isArray(data) ? data : []).map(mapTransferFromApi)));
    } catch (error) {
      console.error('Error loading transfers:', error);
      notifyError(showNotify, error.message || p('load_error'));
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  };

  const transferExportHeaders = [
    { key: 'reference', label: p('col_code') },
    { key: 'date', label: p('col_date') },
    { key: 'banqueSource', label: p('col_source') },
    { key: 'beneficiaire', label: p('col_beneficiary') },
    { key: 'montant', label: p('col_amount') },
    { key: 'frais', label: p('col_fees') },
    { key: 'status', label: p('col_status') },
  ];

  const handleExportCSV = () => { exportBankingRows(filteredTransfers, transferExportHeaders, 'virements', 'csv'); setShowExportDropdown(false); };
  const handleExportExcel = () => { exportBankingRows(filteredTransfers, transferExportHeaders, 'virements', 'excel'); setShowExportDropdown(false); };
  const handleExportPDF = () => { exportBankingPdf(p('title')); setShowExportDropdown(false); };
  const handleImport = () => {
    importBankingRows({
      headers: transferExportHeaders,
      schema: { requiredFields: ['date', 'montant'], validators: { montant: (v) => !v || !Number.isNaN(parseFloat(v)) } },
      mapRow: (row) => mapTransferToApi({
        reference: row.reference,
        date: row.date,
        banqueSource: row.banqueSource,
        banqueDestination: row.banqueDestination || row.banqueSource,
        beneficiaire: row.beneficiaire,
        montant: row.montant,
        frais: row.frais || 0,
        motif: row.motif || row.beneficiaire || 'Import',
        status: row.status || 'pending',
        from_account_id: bankAccounts[0]?.id,
        to_account_id: bankAccounts[1]?.id || bankAccounts[0]?.id,
      }, bankAccounts),
      postRow: (payload) => localApiAdapter.post('/banking/transfers', payload),
      onComplete: loadTransfers,
    });
    setShowImportDropdown(false);
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredTransferIds);
    }
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const generateTransferReference = useCallback(
    () => resolveBanqueEntityReference(settings, 'bankTransfer', 'TRF', transfers, (r) => r.reference),
    [settings, transfers],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const reference = resolveBanqueEntityReference(
        settings,
        'bankTransfer',
        'TRF',
        transfers,
        (r) => r.reference,
        formData.reference,
      );
      if (editingRecord) {
        const apiStatus = formData.status === 'rejected' ? 'cancelled' : formData.status;
        await localApiAdapter.patch(`/banking/transfers/${editingRecord.id}/status`, { status: apiStatus });
        notifyUpdated(showNotify, entityLabel);
      } else {
        const payload = mapTransferToApi({
          ...formData,
          reference,
          motif: formData.transferSystem
            ? `[${formData.transferSystem.toUpperCase()}] ${formData.motif || formData.beneficiaire || ''}`.trim()
            : formData.motif || formData.beneficiaire,
        }, bankAccounts);
        if (!payload.from_account_id || !payload.to_account_id) {
          notifyError(showNotify, p('accounts_required'));
          return;
        }
        if (!formData.montant || Number.isNaN(parseFloat(formData.montant)) || parseFloat(formData.montant) <= 0) {
          notifyError(showNotify, p('amount_required', { defaultValue: 'المبلغ مطلوب' }));
          return;
        }
        if (!formData.beneficiaire?.trim()) {
          notifyError(showNotify, p('label_beneficiary', { defaultValue: 'المستفيد مطلوب' }));
          return;
        }
        await localApiAdapter.post('/banking/transfers', payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadTransfers();
      resetForm();
    } catch (error) {
      console.error('Error saving transfer:', error);
      notifyError(showNotify, error.message || p('save_error'));
    }
  };

  const parseTransferSystem = (motif) => {
    const match = String(motif || '').match(/^\[(SARIE|SADAD|SWIFT)\]/i);
    return match ? match[1].toLowerCase() : 'sarie';
  };

  const handleEdit = (transfer) => {
    setEditingRecord(transfer);
    const system = parseTransferSystem(transfer.motif);
    const cleanMotif = String(transfer.motif || '').replace(/^\[(SARIE|SADAD|SWIFT)\]\s*/i, '');
    setFormData({
      ...transfer,
      transferSystem: system,
      motif: cleanMotif,
      montant: transfer.montant ?? '',
      frais: transfer.frais ?? '',
      from_account_id: transfer.from_account_id || '',
      to_account_id: transfer.to_account_id || '',
    });
    setTransferFormTab('transferInfo');
    setShowModal(true);
  };

  const handleView = (transfer) => {
    setViewingRecord(transfer);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm_single'))) return;
    try {
      await localApiAdapter.patch(`/banking/transfers/${id}/status`, { status: 'cancelled' });
      notifyDeleted(showNotify, entityLabel);
      await loadTransfers();
    } catch (error) {
      console.error('Error cancelling transfer:', error);
      notifyError(showNotify, error.message || p('delete_error'));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(p('delete_confirm_bulk', { count: selectedRecords.length }))) return;
    try {
      await Promise.all(
        selectedRecords.map((id) =>
          localApiAdapter.patch(`/banking/transfers/${id}/status`, { status: 'cancelled' })
        )
      );
      const deleteCount = selectedRecords.length;
      setSelectedRecords([]);
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      await loadTransfers();
    } catch (error) {
      console.error('Error bulk cancelling transfers:', error);
      notifyError(showNotify, error.message || p('delete_error'));
    }
  };

  const accountOptions = bankAccounts.map((a) => ({
    value: String(a.id),
    label: `${a.bank_name || ''} — ${a.account_name || a.account_number}`.trim(),
    isDefault: true,
  }));

  const openNewTransferModal = () => {
    const today = new Date().toISOString().slice(0, 10);
    const fromAcct = bankAccounts[0];
    const toAcct = bankAccounts[1] || bankAccounts[0];
    setEditingRecord(null);
    setTransferFormTab('transferInfo');
    setFormData({
      date: today,
      reference: generateTransferReference(),
      banqueSource: fromAcct ? `${fromAcct.bank_name} — ${fromAcct.account_name}` : '',
      banqueDestination: toAcct ? `${toAcct.bank_name} — ${toAcct.account_name}` : '',
      from_account_id: fromAcct ? String(fromAcct.id) : '',
      to_account_id: toAcct ? String(toAcct.id) : '',
      beneficiaire: '',
      transferSystem: 'sarie',
      montant: '',
      motif: '',
      frais: '',
      status: 'pending',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ date: '', reference: '', banqueSource: '', banqueDestination: '', from_account_id: '', to_account_id: '', transferSystem: 'sarie', beneficiaire: '', montant: '', motif: '', frais: '', status: '' });
    setEditingRecord(null);
    setTransferFormTab('transferInfo');
    setShowModal(false);
  };

  const TRANSFER_FIELD_COUNT = 12;

  const transferFormTabs = useMemo(() => resolveBankingModalFormTabs([
    { id: 'transferInfo', label: p('section_transfer_info'), icon: ArrowLeftRight, iconClass: 'text-emerald-400' },
    { id: 'notes', label: p('section_motif_status'), icon: FileText, iconClass: 'text-purple-400' },
  ], TRANSFER_FIELD_COUNT), [p]);

  const transferStatusOptions = useMemo(() => [
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'completed', label: p('status_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'rejected', label: p('status_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ], [p]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const apiStatus = newStatus === 'rejected' ? 'cancelled' : newStatus;
      await localApiAdapter.patch(`/banking/transfers/${recordId}/status`, { status: apiStatus });
      notifyUpdated(showNotify, entityLabel);
      await loadTransfers();
    } catch (error) {
      console.error('Error updating transfer status:', error);
      notifyError(showNotify, error.message || p('save_error'));
    }
  };

  const renderTransferStatusBadge = (status, recordId, { clickable = true } = {}) => (
    <QawaemClickableStatusBadge
      status={status || 'pending'}
      recordId={recordId}
      options={transferStatusOptions}
      onChange={clickable ? handleStatusChange : undefined}
      title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
    />
  );

  // Calculate stats
  const stats = {
    total: transfers.length,
    completed: transfers.filter(t => t.status === 'completed').length,
    pending: transfers.filter(t => t.status === 'pending').length,
    rejected: transfers.filter(t => t.status === 'rejected').length,
    totalAmount: transfers.reduce((sum, t) => sum + t.montant, 0)
  };

  const mainTabs = [
    { id: 'all_transfers', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: CheckCircle },
    { id: 'par_montant', name: p('tab_by_amount'), icon: DollarSign },
    { id: 'par_banque', name: p('tab_by_bank'), icon: Building2 }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'completed', name: p('filter_completed'), icon: CheckCircle },
    { id: 'pending', name: p('filter_pending'), icon: Clock },
    { id: 'rejected', name: p('filter_rejected'), icon: XCircle }
  ];

  const transferDistribution = {
    all_transfers: {
      titleKey: 'dist_overview',
      data: [
        { id: 'completed', labelKey: 'dist_completed', count: stats.completed, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { id: 'pending', labelKey: 'dist_pending', count: stats.pending, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
        { id: 'rejected', labelKey: 'dist_rejected', count: stats.rejected, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { id: 'total', labelKey: 'dist_total', count: stats.total, color: "bg-gradient-to-r from-blue-500 to-blue-600" }
      ]
    },
    par_statut: {
      titleKey: 'dist_by_status',
      data: [
        { id: 'completed', labelKey: 'dist_completed', count: stats.completed, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { id: 'pending', labelKey: 'dist_pending', count: stats.pending, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
        { id: 'rejected', labelKey: 'dist_rejected', count: stats.rejected, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    par_montant: {
      titleKey: 'dist_by_amount',
      data: [
        { id: 'gt_1m', labelKey: 'amount_gt_1m', count: transfers.filter(t => t.montant >= SAUDI_TRANSFER_AMOUNT_TIERS.gt1m).length, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { id: '500k_1m', labelKey: 'amount_500k_1m', count: transfers.filter(t => t.montant >= SAUDI_TRANSFER_AMOUNT_TIERS.midMin && t.montant < SAUDI_TRANSFER_AMOUNT_TIERS.gt1m).length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { id: 'lt_500k', labelKey: 'amount_lt_500k', count: transfers.filter(t => t.montant < SAUDI_TRANSFER_AMOUNT_TIERS.midMin).length, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_banque: {
      titleKey: 'dist_by_bank',
      data: [
        ...SAUDI_PRIMARY_BANK_CODES.slice(0, 3).map((code, index) => {
          const bank = SAUDI_BANKS.find((b) => b.code === code);
          const colors = [
            'bg-gradient-to-r from-blue-500 to-blue-600',
            'bg-gradient-to-r from-green-500 to-green-600',
            'bg-gradient-to-r from-purple-500 to-purple-600',
          ];
          return {
            id: code.toLowerCase(),
            labelKey: null,
            label: bank?.name || code,
            count: transfers.filter(t => bankMatchesCode(t.banqueSource, code)).length,
            color: colors[index],
          };
        }),
        {
          id: 'others',
          labelKey: 'bank_others',
          count: transfers.filter(t => !SAUDI_PRIMARY_BANK_CODES.slice(0, 3).some((code) => bankMatchesCode(t.banqueSource, code))).length,
          color: 'bg-gradient-to-r from-gray-500 to-gray-600',
        },
      ]
    }
  };

  const distributionInfo = transferDistribution[mainViewMode] || transferDistribution.all_transfers;

  const columnFilterRules = [
    textColumnRule('reference', (row) => row.reference),
    textColumnRule('date', (row) => formatDate(row.date)),
    textColumnRule('beneficiaire', (row) => row.beneficiaire),
    textColumnRule('montant', (row) => String(row.montant ?? '')),
    exactColumnRule('status', (row) => row.status),
  ];

  // Filter transfers
  const baseFilteredTransfers = transfers.filter(transfer => {
    if (viewMode === 'cards' && searchTerm) {
      const term = searchTerm.toLowerCase();
      if (
        !transfer.reference?.toLowerCase().includes(term) &&
        !transfer.beneficiaire?.toLowerCase().includes(term) &&
        !transfer.banqueSource?.toLowerCase().includes(term)
      ) return false;
    }
    
    if (mainViewMode === 'all_transfers') {
      if (quickFilter !== 'all' && transfer.status !== quickFilter) return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_transfers':
        case 'par_statut':
          if (activeDistributionFilter === 'completed' && transfer.status !== 'completed') return false;
          if (activeDistributionFilter === 'pending' && transfer.status !== 'pending') return false;
          if (activeDistributionFilter === 'rejected' && transfer.status !== 'rejected') return false;
          break;
        case 'par_montant':
          if (activeDistributionFilter === 'gt_1m' && transfer.montant < SAUDI_TRANSFER_AMOUNT_TIERS.gt1m) return false;
          if (activeDistributionFilter === '500k_1m' && (transfer.montant < SAUDI_TRANSFER_AMOUNT_TIERS.midMin || transfer.montant >= SAUDI_TRANSFER_AMOUNT_TIERS.gt1m)) return false;
          if (activeDistributionFilter === 'lt_500k' && transfer.montant >= SAUDI_TRANSFER_AMOUNT_TIERS.midMin) return false;
          break;
        case 'par_banque': {
          const primaryCodes = SAUDI_PRIMARY_BANK_CODES.slice(0, 3);
          if (activeDistributionFilter === 'others') {
            if (primaryCodes.some((code) => bankMatchesCode(transfer.banqueSource, code))) return false;
          } else {
            const matchedCode = primaryCodes.find((code) => code.toLowerCase() === activeDistributionFilter);
            if (matchedCode && !bankMatchesCode(transfer.banqueSource, matchedCode)) return false;
          }
          break;
        }
      }
    }
    
    return true;
  });

  const filteredTransfers = applyColumnFilters(baseFilteredTransfers, columnFilters, columnFilterRules)
    .sort((a, b) => b.id - a.id);

  const filteredTransferIds = useMemo(() => filteredTransfers.map((tr) => tr.id), [filteredTransfers]);
  const allRowsSelected = filteredTransferIds.length > 0 && filteredTransferIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const qawaemStats = useMemo(() => sliceQawaemStats([
    { key: 'total', label: p('stat_total'), value: stats.total, icon: ArrowLeftRight, variant: 'copper' },
    { key: 'completed', label: p('stat_completed'), value: stats.completed, icon: CheckCircle, variant: 'forest' },
    { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, variant: 'copper' },
    { key: 'amount', label: p('stat_total_amount'), value: formatAmount(stats.totalAmount), icon: DollarSign, variant: 'forest' },
  ], 4), [stats, p, formatAmount]);

  const qawaemColumnDefs = useMemo(() => [
    { key: 'reference', label: p('col_code'), checked: columnVisibility.reference !== false },
    { key: 'date', label: p('col_date'), checked: columnVisibility.date !== false },
    { key: 'banqueSource', label: p('col_source'), checked: columnVisibility.banqueSource !== false },
    { key: 'beneficiaire', label: p('col_beneficiary'), checked: columnVisibility.beneficiaire !== false },
    { key: 'montant', label: p('col_amount'), checked: columnVisibility.montant !== false },
    { key: 'frais', label: p('col_fees'), checked: columnVisibility.frais !== false },
    { key: 'actions', label: p('col_actions'), checked: true, required: true },
  ], [columnVisibility, p]);
  const handleToggleColumn = (key) => {
    if (key === 'actions') return;
    const col = qawaemColumnDefs.find((c) => c.key === key);
    if (col?.required) return;
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => columnVisibility[key] !== false;
  const tableColSpan = 1 + ['reference', 'date', 'banqueSource', 'beneficiaire', 'montant', 'frais'].filter((k) => isColVisible(k)).length + 1;

  return (
    <QawaemErpListPageShell
      className="space-y-3"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={ArrowLeftRight}
      stats={qawaemStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
          title={t('common.delete', { defaultValue: 'حذف' })}
        >
          <Trash2 size={16} />
          <span>{t('common.delete')} ({selectedRecords.length})</span>
        </button>
      ) : null}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
      onCreate={openNewTransferModal}
      createLabel={p('new_transfer')}
      headerActions={(
        <button
          type="button"
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold flex items-center gap-2`}
        >
          <Filter className="h-5 w-5" />
          <span className="hidden sm:inline">{p('advanced_options')}</span>
        </button>
      )}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import'),
        exportLabel: p('export'),
        onImportCSV: handleImport,
        onImportExcel: handleImport,
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      }}
    >

      {/* Advanced Options */}
      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_transfers' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{p(distributionInfo.titleKey)}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.id} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.id ? null : item.id)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.id ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.id ? 'text-white' : 'text-white/80'}`}>{item.labelKey ? p(item.labelKey) : item.label}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.id ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Main Table */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColVisible('reference') ? (
                    <TableColumnHeaderFilter columnKey="reference" label={p('col_code')} value={columnFilters.reference || ''} onChange={(v) => setFilter('reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('date') ? (
                    <TableColumnHeaderFilter columnKey="date" label={p('col_date')} value={columnFilters.date || ''} onChange={(v) => setFilter('date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('banqueSource') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_source')}</th>
                  ) : null}
                  {isColVisible('beneficiaire') ? (
                    <TableColumnHeaderFilter columnKey="beneficiaire" label={p('col_beneficiary')} value={columnFilters.beneficiaire || ''} onChange={(v) => setFilter('beneficiaire', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('montant') ? (
                    <TableColumnHeaderFilter columnKey="montant" label={p('col_amount')} value={columnFilters.montant || ''} onChange={(v) => setFilter('montant', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('frais') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_fees')}</th>
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredTransfers.length > 0 ? (
                  filteredTransfers.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      {isColVisible('reference') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.reference}</span></td>
                      ) : null}
                      {isColVisible('date') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {formatDate(record.date)}
                            </div>
                          </div>
                        </td>
                      ) : null}
                      {isColVisible('banqueSource') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.banqueSource || '-'}</span></td>
                      ) : null}
                      {isColVisible('beneficiaire') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.beneficiaire}</span></td>
                      ) : null}
                      {isColVisible('montant') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-bold text-lg">{formatAmount(record.montant)}</span></div></td>
                      ) : null}
                      {isColVisible('frais') ? (
                        <td className="px-6 py-4"><span className="text-red-400">{formatAmount(record.frais)}</span></td>
                      ) : null}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex items-center justify-center gap-2">
                          {renderTransferStatusBadge(record.status, record.id)}
                          <QawaemErpRowActions
                            onView={() => handleView(record)}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><ArrowLeftRight size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_state')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_transfers', { count: filteredTransfers.length })}</span>
                      <span>{p('footer_total_amount')} <span className="text-white font-semibold">{formatAmount(filteredTransfers.reduce((sum, t) => sum + (parseFloat(t.montant) || 0), 0))}</span></span>
                      <span>{p('footer_total_fees')} <span className="text-red-400 font-semibold">{formatAmount(filteredTransfers.reduce((sum, t) => sum + (parseFloat(t.frais) || 0), 0))}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTransfers.length > 0 ? (
              filteredTransfers.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><ArrowLeftRight size={20} className="text-emerald-400" />{record.reference}</h3>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-white/70 text-sm">
                          <Clock size={14} className="text-white/50" />
                          {formatDate(record.date)}
                        </div>
                        <div className="text-white/70 text-sm"><span className="font-medium">{p('card_source')}</span> {record.banqueSource}</div>
                        <div className="text-white/70 text-sm"><span className="font-medium">{p('card_destination')}</span> {record.banqueDestination}</div>
                        <div className="text-white/70 text-sm"><span className="font-medium">{p('col_beneficiary')}:</span> {record.beneficiaire}</div>
                        <div className="text-white font-bold text-lg">{formatAmount(record.montant)}</div>
                        <div className="text-red-400 text-sm">{p('card_fees')} {formatAmount(record.frais)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-4">
                      {renderTransferStatusBadge(record.status, record.id)}
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleView(record)}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><ArrowLeftRight size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_state')}</p></div>
            )}
          </div>
        )}
      </div>

      {/* View Modal — guarded wrap (AGENTS §8) */}
      {showViewModal && viewingRecord ? (
        ReactDOM.createPortal(
          <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)}>
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.2 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-white">{p('modal_details')}</h2>
                  <button type="button" onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('label_code')}</p><p className="text-white font-medium font-mono">{viewingRecord.reference}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_date')}</p><p className="text-white font-medium">{formatDate(viewingRecord.date)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_transfer_system')}</p><p className="text-white font-medium">{parseTransferSystem(viewingRecord.motif).toUpperCase()}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_source_bank')}</p><p className="text-white font-medium">{viewingRecord.banqueSource || '-'}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_dest_bank')}</p><p className="text-white font-medium">{viewingRecord.banqueDestination || '-'}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_beneficiary')}</p><p className="text-white font-medium">{viewingRecord.beneficiaire || '-'}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_amount')}</p><p className="text-emerald-400 font-bold text-xl">{formatAmount(viewingRecord.montant)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_fees')}</p><p className="text-red-400 font-bold text-xl">{formatAmount(viewingRecord.frais)}</p></div>
                    {viewingRecord.motif ? (
                      <div className="md:col-span-3"><p className="text-white/60 text-sm mb-1">{p('label_motif')}</p><p className="text-white/70">{viewingRecord.motif}</p></div>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )
      ) : null}

      {/* Add/Edit Modal — tabs (Products.js pattern) */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="transfer-form" data-testid="bank-transfer-submit" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{editingRecord ? p('btn_update') : p('btn_create')}</button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <div className="shrink-0 px-6 pt-3 border-b border-white/10">
              <div className="flex flex-wrap gap-1" role="tablist" aria-label={editingRecord ? p('modal_edit') : p('modal_new')}>
                {transferFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = transferFormTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setTransferFormTab(tab.id)}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${
                        active
                          ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px'
                          : 'text-white/55 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <TabIcon size={16} className={active ? tab.iconClass : ''} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="transfer-form" data-testid="bank-transfer-form" noValidate onSubmit={handleSubmit}>
                {transferFormTab === 'transferInfo' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_transfer_system')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="bank-transfer-system" value={formData.transferSystem || 'sarie'} onChange={(value) => setFormData({ ...formData, transferSystem: value })} options={transferSystemOptions} searchable={false} autoSelect={false} placeholder={p('select_or_type')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_code')} <span className="qawaem-required-mark">*</span></label><input type="text" data-testid="bank-transfer-reference" required value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono" placeholder={p('placeholder_auto_generated')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_date')} <span className="qawaem-required-mark">*</span></label><input type="date" required value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_beneficiary')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="bank-transfer-beneficiary" value={formData.beneficiaire} onChange={(value) => setFormData({ ...formData, beneficiaire: value })} options={beneficiaryOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('beneficiary_placeholder')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_source_bank')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="bank-transfer-from-account" value={String(formData.from_account_id || '')} onChange={(value) => { const acct = bankAccounts.find((a) => String(a.id) === String(value)); setFormData({ ...formData, from_account_id: value, banqueSource: acct ? `${acct.bank_name} — ${acct.account_name}` : value }); }} options={accountOptions} searchable={true} creatable={false} autoSelect={false} placeholder={p('select_or_type')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_dest_bank')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="bank-transfer-to-account" value={String(formData.to_account_id || '')} onChange={(value) => { const acct = bankAccounts.find((a) => String(a.id) === String(value)); setFormData({ ...formData, to_account_id: value, banqueDestination: acct ? `${acct.bank_name} — ${acct.account_name}` : value }); }} options={accountOptions} searchable={true} creatable={false} autoSelect={false} placeholder={p('select_or_type')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_amount_sar')} <span className="qawaem-required-mark">*</span></label><CustomNumberInput data-testid="bank-transfer-amount" step={0.01} value={formData.montant || ''} onChange={(value) => setFormData({ ...formData, montant: value })} placeholder="0.00" /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_fees_sar')}</label><CustomNumberInput data-testid="bank-transfer-fees" step={0.01} value={formData.frais || ''} onChange={(value) => setFormData({ ...formData, frais: value })} placeholder="0.00" /></div>
                  </div>
                )}
                {transferFormTab === 'notes' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-3"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_motif')}</label><textarea value={formData.motif} onChange={(e) => setFormData({ ...formData, motif: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('motif_placeholder')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="bank-transfer-status" value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={statusFormOptions} searchable={false} autoSelect={false} /></div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </QawaemErpListPageShell>
  );
};

export default BankTransfersPage;


